import type { ReactNode } from 'react';

import type { GetItemPropsOptions } from 'downshift';

import type {
  AutocompleteGetItemProps,
  DefaultItem,
  DropdownGetItemProps,
  GenericItem,
} from '../types';

type ExtraOptions = {
  index?: number;
  isHighlighted: boolean;
  text?: ReactNode;
  subtext?: ReactNode;
};

/**
 * Wrapper for getItemProps which adds the "isHighlighted" property on the given item.
 * @param args.getItemProps prop getter returned from downshift hook
 * @param args.highlightedIndex prop returned from downshift hook
 * @returns modified version of getItemProps
 */
export const wrapGetItemProps =
  <TItem extends GenericItem>({
    getItemProps,
    highlightedIndex,
    selectedItem,
    indexGetter,
  }: {
    getItemProps:
      | DropdownGetItemProps<TItem, ExtraOptions>
      | AutocompleteGetItemProps<TItem, ExtraOptions>;
    highlightedIndex: number;
    selectedItem: TItem | null;
    indexGetter: null | ((item: TItem) => number | undefined);
  }) =>
  ({
    item,
    index: indexProp,
    isSelected: isSelectedProp,
    ...rest
  }: GetItemPropsOptions<TItem>) => {
    const index = indexProp ?? indexGetter?.(item);

    const toReturn = getItemProps({
      item,
      index,
      isHighlighted: index === highlightedIndex,
      isSelected: isSelectedProp ?? selectedItem === item,
      text: (item as unknown as DefaultItem).label,
      subtext: (item as unknown as DefaultItem).subtext,
      ...rest,
    });

    return {
      ...toReturn,
      disabled: toReturn['aria-disabled'],
    };
  };
